/**
 * When a logged activity happened.
 *
 * activities.created_at is `timestamp without time zone` holding UTC, and the
 * browser renders it in local time. So the instant written for "the call was on
 * Sep 22" has to read back as Sep 22 in the viewer's zone. Midnight UTC does
 * not: it is the previous evening anywhere in the Americas, which is how every
 * back-dated COI touch displayed a day early.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Allowed skew for a client clock or zone boundary; beyond it is a typo, not a log. */
const FUTURE_SKEW_MS = 36 * 60 * 60 * 1000;

function splitYmd(ymd: string): [number, number, number] | null {
  const m = YMD.exec(ymd);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return [y, mo, d];
}

/**
 * A date-only value from a server path that has no viewer zone: noon UTC is
 * the same calendar day from Hawaii to Maine.
 */
export function dateOnlyToUtcNoon(ymd: string): string | null {
  const parts = splitYmd(ymd);
  if (!parts) return null;
  const [y, mo, d] = parts;
  return new Date(Date.UTC(y, mo - 1, d, 12)).toISOString();
}

/**
 * The log form's date picker, resolved in the browser: the picked local day at
 * the current local time of day. Today therefore means now, which keeps a call
 * just logged above one logged this morning.
 */
export function occurredAtFromLocalDate(ymd: string, now: Date = new Date()): string {
  const parts = splitYmd(ymd);
  if (!parts) return now.toISOString();
  const [y, mo, d] = parts;
  const at = new Date(now);
  at.setFullYear(y, mo - 1, d);
  return at.toISOString();
}

export type OccurredAtResult = { ok: true; value: string | null } | { ok: false; error: string };

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

/** Server-side check of a client-sent occurredAt. null means "use now()". */
export function parseOccurredAt(value: unknown, now: Date = new Date()): OccurredAtResult {
  if (value === undefined || value === null || value === "") return { ok: true, value: null };
  if (typeof value !== "string" || !ISO_INSTANT.test(value)) {
    return { ok: false, error: "occurredAt must be an ISO timestamp with a zone" };
  }
  const t = Date.parse(value);
  if (Number.isNaN(t)) return { ok: false, error: "occurredAt is not a valid date" };
  if (t - now.getTime() > FUTURE_SKEW_MS) return { ok: false, error: "occurredAt is in the future" };
  return { ok: true, value: new Date(t).toISOString() };
}
